import { useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field'
import { Notice } from '@/components/ui/Notice'
import { OBSERVATION_SITES } from '@/config/eclipse'
import { SITE } from '@/config/site'
import { useI18n } from '@/i18n'
import {
  isBackendConnected,
  submitRegistration,
  type RegistrationRole,
} from '@/lib/submissions'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const ROLES: RegistrationRole[] = ['participant', 'volunteer', 'researcher', 'partner', 'media']

/**
 * Expedition / volunteer registration.
 *
 * Posts through the submission adapter in `@/lib/submissions`, which currently
 * has no backend behind it — see `docs/BACKEND_PLAN.md`.
 */
export function RegistrationForm() {
  const { t, pick, locale } = useI18n()
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)

    setStatus('submitting')
    setError(null)

    const result = await submitRegistration({
      fullName: String(data.get('full_name') ?? '').trim(),
      email: String(data.get('email') ?? '')
        .trim()
        .toLowerCase(),
      organization: String(data.get('organization') ?? '').trim() || null,
      role: String(data.get('role') ?? 'participant') as RegistrationRole,
      preferredSiteId: String(data.get('preferred_site_id') ?? '') || null,
      message: String(data.get('message') ?? '').trim() || null,
      locale,
    })

    if (!result.ok) {
      setStatus('error')
      setError(
        result.reason === 'not-configured'
          ? `${t.form.errorOffline} ${SITE.email}`
          : t.form.errorGeneric,
      )
      return
    }

    setStatus('success')
    form.reset()
  }

  if (status === 'success') {
    return (
      <Card className="text-center">
        <p className="font-serif text-2xl text-gold">{t.form.successTitle}</p>
        <p className="mt-2 text-sm text-muted">{t.form.successBody}</p>
      </Card>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {!isBackendConnected ? (
        <Notice>
          {t.form.errorOffline}{' '}
          <a
            href={`mailto:${SITE.email}`}
            className="break-all text-gold underline underline-offset-4"
          >
            {SITE.email}
          </a>
        </Notice>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label={t.form.name} name="full_name" required autoComplete="name" />
        <TextField label={t.form.email} name="email" type="email" required autoComplete="email" />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          label={t.form.organization}
          hint={t.form.optional}
          name="organization"
          autoComplete="organization"
        />
        <SelectField label={t.form.role} name="role" defaultValue="participant" required>
          {ROLES.map((role) => (
            <option key={role} value={role} className="bg-surface">
              {t.form.roles[role]}
            </option>
          ))}
        </SelectField>
      </div>

      <SelectField label={t.form.site} hint={t.form.optional} name="preferred_site_id" defaultValue="">
        <option value="" className="bg-surface">
          —
        </option>
        {OBSERVATION_SITES.map((site) => (
          <option key={site.id} value={site.id} className="bg-surface">
            {site.city}, {pick(site.country)}
          </option>
        ))}
      </SelectField>

      <TextAreaField label={t.form.message} hint={t.form.optional} name="message" />

      {error ? (
        <p role="alert" className="text-sm text-ember">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={status === 'submitting' || !isBackendConnected}
        className="w-full sm:w-auto"
      >
        {status === 'submitting' ? t.form.submitting : t.form.submit}
      </Button>
    </form>
  )
}
